import {
  type ContextType,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';
import type { z } from 'zod';
import type {
  ExpenditureNatureBaseModel,
  RevenueNatureBaseModel,
  RevenueNatureFormBaseSchema,
} from '../models';

// TODO: Unify path and value
// FLY: https://github.com/weldrow-com/bns/pull/7645#discussion_r1624100089
const TableTypeModifier = {
  accountingType: 'accounting-type',
  budgetType: 'budget-type',
  decidedBudget: 'decided-budget',
  expenditureNature: 'expenditure-nature',
  expenditurePurpose: 'expenditure-purpose',
  expenditureBudgetPurpose: 'expenditure-budget-purpose',
  expenditureBudgetJurisdiction: 'expenditure-budget-jurisdiction',
  expenditureBudgetOrganization: 'expenditure-budget-organization',
  accountTitle: 'account-title',
  policy: 'policy',
  revenueSource: 'revenue-source',
  revenueNature: 'revenue-nature',
  cost: 'cost',
  expenditureItemNaturePreset: 'expenditure-item-nature-preset',
  expenditurePurposeOfSettlementStatisticsPreset:
    'expenditure-purpose-of-settlement-statistics-preset',
  indicatorMeasurement: 'indicator-measurement',
  ministry: 'ministry',
  majorMatter: 'major-matter',
  expenditureItemMajorMatter: 'expenditure-item-major-matter',
  reservedProject: 'reserved-project',
  frameAllocationBreakdownLabel: 'frame-allocation-breakdown-label',
  frameAllocationCategory: 'frame-allocation-category',
  frameAllocationCategoryGroup: 'frame-allocation-category-group',
} as const satisfies Record<string, string>;

export type TableType =
  (typeof TableTypeModifier)[keyof typeof TableTypeModifier];

type DrawerState =
  | { open: false }
  | {
      open: true;
      type: Exclude<TableType, 'expenditure-nature' | 'revenue-nature'>;
      isEditing: false;
    }
  | {
      open: true;
      type: Extract<TableType, 'expenditure-nature'>;
      isEditing: false;
      creatingLevel?: Exclude<
        z.infer<typeof ExpenditureNatureBaseModel.shape.level>,
        0
      >;
      upperId?: z.infer<typeof ExpenditureNatureBaseModel.shape.upperId>;
    }
  | {
      open: true;
      type: Extract<TableType, 'revenue-nature'>;
      isEditing: false;
      creatingLevel?: Exclude<
        z.infer<typeof RevenueNatureFormBaseSchema.shape.level>,
        1
      >;
      creatingUpperId?: z.infer<typeof RevenueNatureBaseModel.shape.upperId>;
    }
  | {
      open: true;
      type: Extract<TableType, 'expenditure-purpose'>;
      isEditing: false;
      upperId?: string;
    }
  | { open: true; type: TableType; isEditing: true; id: string }
  | {
      open: true;
      type: Extract<TableType, 'revenue-source'>;
      isEditing: true;
      revenueSourceId: string;
      id: string;
    };

const Context = createContext<
  | {
      drawerState: DrawerState;
      setDrawerState: Dispatch<SetStateAction<DrawerState>>;
    }
  | undefined
>(undefined);

const _useContext = (): NonNullable<ContextType<typeof Context>> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useDrawerState must be used within DrawerProvider.');
  }
  return context;
};

export const useDrawerState = () => {
  return _useContext();
};

export const DrawerStateProvider: FC<PropsWithChildren> = ({ children }) => {
  const [drawerState, setDrawerState] = useState<DrawerState>({ open: false });

  return (
    <Context.Provider value={{ drawerState, setDrawerState }}>
      {children}
    </Context.Provider>
  );
};
